const MARKER_LABEL_CHAR_PX = 6.4;
const MARKER_LABEL_PAD_PX = 10;
const MARKER_LABEL_GAP_PX = 8;

function markerLabelWidth(label: string, maxChars?: number) {
  const text = maxChars && label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label;
  return { text, width: Math.max(36, text.length * MARKER_LABEL_CHAR_PX + MARKER_LABEL_PAD_PX) };
}

/** Place event labels in lanes; flip left near the right edge so text stays inside the plot. */
export function layoutMarkerLanes<T extends { x: number; label: string }>(
  events: T[],
  plotLeft: number,
  plotRight: number,
): Array<T & { lane: number; textX: number; textAnchor: "start" | "end"; displayLabel: string }> {
  const sorted = [...events].sort((a, b) => a.x - b.x || a.label.localeCompare(b.label));
  const laneSpans: Array<Array<{ left: number; right: number }>> = [];

  const fitsLane = (lane: number, left: number, right: number) => {
    const spans = laneSpans[lane] || [];
    return !spans.some((s) => right > s.left - 4 && left < s.right + 4);
  };

  const placeOnLane = (lane: number, left: number, right: number) => {
    if (!laneSpans[lane]) laneSpans[lane] = [];
    laneSpans[lane].push({ left, right });
  };

  return sorted.map((ev) => {
    const plotW = Math.max(plotRight - plotLeft, 40);
    const maxChars = Math.max(8, Math.floor((plotW * 0.22) / MARKER_LABEL_CHAR_PX));
    const { text: displayLabel, width } = markerLabelWidth(ev.label, maxChars);

    const rightIfStart = ev.x + MARKER_LABEL_GAP_PX + width;
    const leftIfEnd = ev.x - MARKER_LABEL_GAP_PX - width;
    let textAnchor: "start" | "end" = "start";
    let textX = ev.x + MARKER_LABEL_GAP_PX;
    let left = textX;
    let right = textX + width;

    if (rightIfStart > plotRight && leftIfEnd >= plotLeft) {
      textAnchor = "end";
      textX = ev.x - MARKER_LABEL_GAP_PX;
      right = textX;
      left = textX - width;
    } else if (rightIfStart > plotRight) {
      textAnchor = "end";
      textX = plotRight;
      right = textX;
      left = textX - width;
    } else {
      textX = Math.max(plotLeft, Math.min(textX, plotRight - width));
      left = textX;
      right = textX + width;
    }

    let lane = 0;
    while (!fitsLane(lane, left, right)) lane += 1;
    placeOnLane(lane, left, right);

    return { ...ev, lane, textX, textAnchor, displayLabel };
  });
}
